import {
  PREPAID_TIER_MULTIPLIERS,
  getEconomyTierMultiplier,
  getStandardTierMultiplier,
} from '../../../../../shared-pricing-config'
import type { ProviderTier } from '../../../../../shared-pricing-config'
import type { UnifiedModel } from '../types/unified-model.types'

/**
 * Single source of truth for tier price math, shared by ModelCard,
 * Model cards and the model detail page so the numbers can never drift
 * between views.
 */

export const economyTierTooltip = (discountPercent: number) =>
  `Economy tier is ${discountPercent}% off official pricing, but only when paid from your Prepaid balance. Bonus balance (referral rewards, gift codes) is charged at full price on Economy tier.`

export const standardTierTooltip = (discountPercent: number) =>
  `Standard tier is ${discountPercent}% off official pricing, but only when paid from your Prepaid balance. Bonus balance (referral rewards, gift codes) is not discounted on Standard tier.`

export function getDiscountedPrice(
  price: number,
  hasDiscount: boolean | undefined,
  discountPercent: number | undefined,
): number {
  const d = (discountPercent ?? 0) / 100
  return hasDiscount ? price * (1 - d) : price
}

export function getTierMultipliers(modelId: string) {
  return {
    economy: getEconomyTierMultiplier(modelId),
    standard: getStandardTierMultiplier(modelId),
    premium: PREPAID_TIER_MULTIPLIERS.premium,
  }
}

export function getTierDiscountPercents(modelId: string) {
  const { economy, standard } = getTierMultipliers(modelId)
  return {
    economy: Math.round((1 - economy) * 100),
    standard: Math.round((1 - standard) * 100),
  }
}

/**
 * The lowest-priced tier a model is available on. Falls back to premium
 * (official rate) when tier availability is unknown.
 */
export function getCheapestTier(
  modelId: string,
  availableTiers: Array<ProviderTier> | undefined,
): { tier: ProviderTier; multiplier: number } {
  const multipliers = getTierMultipliers(modelId)
  const tiers: Array<ProviderTier> = availableTiers?.length
    ? availableTiers
    : ['premium']
  const tier = tiers.reduce((cheapest, candidate) =>
    multipliers[candidate] < multipliers[cheapest] ? candidate : cheapest,
  )
  return { tier, multiplier: multipliers[tier] }
}

/**
 * Headline discount for a catalog card: campaign discount combined with the
 * cheapest available tier (chat only — generation pricing has no tiers).
 */
export function getCardDiscountPercent(
  model: Pick<UnifiedModel, 'id' | 'type' | 'hasDiscount' | 'discountPercent'>,
  availableTiers: Array<ProviderTier> | undefined,
): number {
  const hasCampaign = !!model.hasDiscount && (model.discountPercent ?? 0) > 0
  const campaignMultiplier = hasCampaign
    ? 1 - (model.discountPercent ?? 0) / 100
    : 1
  if (model.type !== 'chat') {
    return Math.round((1 - campaignMultiplier) * 100)
  }
  const { multiplier } = getCheapestTier(model.id, availableTiers)
  return Math.round((1 - multiplier * campaignMultiplier) * 100)
}
